import Box from "@mui/material/Box";
import { useState } from "react";
import { actionLayerUpdate } from "./LayerRedux";
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid";
import Collapse from "@mui/material/Collapse";
import SettingsIcon from "@mui/icons-material/Settings";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Input from "@mui/material/Input";
import ReplayIcon from "@mui/icons-material/Replay";
import { defaultState } from "./LayerData";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import SwatchesPicker from "../Shared/SwatchesPicker";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

const container_ = (theme) => ({
  width: "100%",
});

const button_ = (theme) => ({
  display: "block",
  marginLeft: "auto",
  "& .MuiButton-label": {
    fontSize: "0.7rem",
  },
});

const root_ = (theme) => ({
  padding: "20px",
  backgroundColor: "rgba(0, 0, 0, 0.05)",
  borderRadius: "4px",
});

const input_ = (theme) => ({
  width: 42,
});

const icon_ = (theme) => ({
  "&:hover": {
    cursor: "pointer",
  },
});

const colorIndicator_ = (theme) => ({
  width: 25,
  height: 25,
  "&:hover": {
    cursor: "pointer",
  },
});

const colorPalette_ = (theme) => ({
  position: "relative",
});

const centerGrid_ = (theme) => ({
  alignItems: "center",
});

export default function LabelSettings() {
  const [expand, setExpand] = useState(false);
  const onClick = () => {
    setExpand(!expand);
  };

  return (
    <Box sx={container_} style={{ marginBottom: expand ? 10 : -30 }}>
      <Tooltip title="Font Settings">
        <Button
          color="primary"
          size="small"
          sx={button_}
          onClick={onClick}
          startIcon={<SettingsIcon />}
        >
          Font Settings
        </Button>
      </Tooltip>
      <Collapse in={expand}>
        <Box sx={root_}>
          <FontSizeControl />
          <FontZoomSyncControl />
          <FontColorControl />
          <Box style={{ textAlign: "center" }}>
            <Button size="small" onClick={onClick}>
              Hide Settings
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

function FontSizeControl() {
  const dispatch = useDispatch();
  const fontSize = useSelector(
    (state) => state.layerReducer.labelSettings.fontSize,
  );

  const handleSliderChange = (event, newValue) => {
    dispatch(
      actionLayerUpdate({
        type: "label-settings",
        field: "fontSize",
        value: newValue,
      }),
    );
  };

  const handleInputChange = (event) => {
    const newValue =
      event.target.value === "" ? "" : Number(event.target.value);
    dispatch(
      actionLayerUpdate({
        type: "label-settings",
        field: "fontSize",
        value: newValue,
      }),
    );
  };

  const handleBlur = () => {
    if (fontSize < 1) {
      dispatch(
        actionLayerUpdate({
          type: "label-settings",
          field: "fontSize",
          value: 1,
        }),
      );
    }
  };

  const resetHandler = () => {
    dispatch(
      actionLayerUpdate({
        type: "label-settings",
        field: "fontSize",
        value: defaultState.labelPrimarySize,
      }),
    );
  };

  return (
    <>
      <Typography
        id="input-label-font-size-slider"
        variant="overline"
        gutterBottom
      >
        Font Size
      </Typography>
      <Grid container spacing={2} sx={centerGrid_}>
        <Grid size="grow">
          <Slider
            value={typeof fontSize === "number" ? fontSize : 1}
            onChange={handleSliderChange}
            aria-labelledby="input-label-font-size-slider"
            marks={true}
            step={1}
            min={1}
            max={30}
            size="small"
          />
        </Grid>
        <Grid>
          <Input
            sx={input_}
            value={fontSize}
            size="small"
            onChange={handleInputChange}
            onBlur={handleBlur}
            inputProps={{
              step: 1,
              min: 1,
              max: 100,
              type: "number",
              "aria-labelledby": "input-label-font-size-slider",
            }}
          />
        </Grid>
        <Grid>
          <Tooltip title="Reset">
            <ReplayIcon onClick={resetHandler} sx={icon_} />
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
}
function FontColorControl() {
  const dispatch = useDispatch();
  const fontColor = useSelector(
    (state) => state.layerReducer.labelSettings.fontColor,
  );
  const [showPalette, setShowPalette] = useState(false);

  const togglePalette = () => {
    setShowPalette(!showPalette);
  };
  const handleClose = () => {
    setShowPalette(false);
  };

  const onChangeComplete = (color) => {
    dispatch(
      actionLayerUpdate({
        type: "label-settings",
        field: "fontColor",
        value: color.hex,
      }),
    );
  };
  const resetHandler = () => {
    dispatch(
      actionLayerUpdate({
        type: "label-settings",
        field: "fontColor",
        value: defaultState.labelColor,
      }),
    );
  };

  return (
    <>
      <Typography
        id="input-label-font-size-slider"
        variant="overline"
        gutterBottom
      >
        Color
      </Typography>
      <ClickAwayListener onClickAway={handleClose}>
        <Grid container spacing={2} sx={centerGrid_}>
          <Grid style={{ paddingTop: "2px" }}>
            <Box
              style={{ backgroundColor: fontColor }}
              sx={colorIndicator_}
              onClick={togglePalette}
            ></Box>
          </Grid>
          <Grid>
            <Tooltip title="Reset">
              <ReplayIcon onClick={resetHandler} sx={icon_} />
            </Tooltip>
          </Grid>
          <Grid>
            <Box
              style={{ display: showPalette ? "block" : "none" }}
              sx={colorPalette_}
            >
              <SwatchesPicker
                color={fontColor}
                onChangeComplete={onChangeComplete}
              />
            </Box>
          </Grid>
        </Grid>
      </ClickAwayListener>
    </>
  );
}
function FontZoomSyncControl() {
  const dispatch = useDispatch();
  const fontSizeZoomSync = useSelector(
    (state) => state.layerReducer.labelSettings.fontSizeZoomSync,
  );

  const handleChange = (event) => {
    dispatch(
      actionLayerUpdate({
        type: "label-settings",
        field: "fontSizeZoomSync",
        value: event.target.checked,
      }),
    );
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={fontSizeZoomSync}
            onChange={handleChange}
            name="fontSizeZoomSync"
            color="primary"
          />
        }
        label="Scale with Zoom"
        slotProps={{ typography: { variant: "overline" } }}
        labelPlacement="end"
      />
    </Box>
  );
}
